// src/components/cookies/CookiePreferencesModal.jsx
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { IOSSwitch } from "../common/IOSSwitch";

const CookiePreferencesModal = ({
  open,
  onClose,
  preferences,
  setPreferences,
  onSave,
}) => {
  const { t } = useTranslation();
  const savePreferences = (prefs) => {
    document.cookie = `cookie_preferences_facialsense=${encodeURIComponent(
      JSON.stringify(prefs)
    )}; path=/; max-age=31536000`;

    // 🔥 báo cho toàn app biết
    window.dispatchEvent(new Event("cookie-consent-changed"));
  };

  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = original);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="
          relative w-full max-w-[60vw] md:max-w-[80vw]  rounded-2xl
          border border-white/10 shadow-2xl
          bg-[#293990] text-white
          max-h-[80vh] flex flex-col
        "
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--tree-poppy)]">
          <h2 className="text-[18px] font-bold">
            {t("cookies.preferencesTitle")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-xl hover:opacity-70 transition"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6 text-sm overflow-y-auto">
          {/* Strict */}
          <div className="flex items-center justify-between gap-6">
            <div>
              <p className="font-bold text-[16px]">
                {t("cookies.strictlyNecessary")}
              </p>
              <p className="mt-1 opacity-80 text-[15px]">
                {t("cookies.strictlyNecessaryDesc")}
              </p>
            </div>
            <span className="text-[var(--tree-poppy)] text-[16px] font-semibold whitespace-nowrap">
              {t("cookies.alwaysActive")}
            </span>
          </div>

          {/* Functional */}
          <Row
            label={t("cookies.functional")}
            desc={t("cookies.functionalDesc")}
          >
            <IOSSwitch
              checked={preferences.functional}
              onChange={(e) =>
                setPreferences((p) => ({
                  ...p,
                  functional: e.target.checked,
                }))
              }
            />
          </Row>

          {/* Analytics */}
          <Row
            label={t("cookies.analytics")}
            desc={t("cookies.analyticsDesc")}
          >
            <IOSSwitch
              checked={preferences.analytics}
              onChange={(e) =>
                setPreferences((p) => ({
                  ...p,
                  analytics: e.target.checked,
                }))
              }
            />
          </Row>

          {/* Marketing */}
          <Row
            label={t("cookies.marketing")}
            desc={t("cookies.marketingDesc")}
          >
            <IOSSwitch
              checked={preferences.marketing}
              onChange={(e) =>
                setPreferences((p) => ({
                  ...p,
                  marketing: e.target.checked,
                }))
              }
            />
          </Row>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--tree-poppy)]">
          <button
            type="button"
            onClick={onClose}
            className="p-[10px] h-[48px] text-[15px] rounded-md border border-white/20 hover:bg-white/10"
          >
            {t("cookies.cancel")}
          </button>

          <button
            type="button"
            onClick={() => {
              savePreferences(preferences);
              onSave?.(preferences); // optional
              onClose();
            }}
            className="
              p-[10px] h-[48px] text-[15px] rounded-md font-medium
              bg-[var(--tree-poppy)] text-white
            "
          >
            {t("cookies.savePreferences")}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===== PropTypes cho Modal ===== */
CookiePreferencesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  setPreferences: PropTypes.func.isRequired,
  preferences: PropTypes.shape({
    functional: PropTypes.bool.isRequired,
    analytics: PropTypes.bool.isRequired,
    marketing: PropTypes.bool.isRequired,
  }).isRequired,
};

/* ===== Row component ===== */
const Row = ({ label, desc, children }) => (
  <div className="flex items-center gap-4">
    <div className="flex-1 max-w-[100%]">
      <p className="text-[16px] font-bold leading-snug">{label}</p>
      <p className="mt-1 text-[15px] opacity-80 leading-snug">{desc}</p>
    </div>

    <div className="shrink-0 pt-1">{children}</div>
  </div>
);

Row.propTypes = {
  label: PropTypes.string.isRequired,
  desc: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default CookiePreferencesModal;
